import React, { useCallback, useEffect, useRef } from "react";
import { DatePicker } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { Controller, useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import ReportDocumentControl from "~/pages/BonusQReport/Components/ReportDocumentControl";
import { useSetAppeal } from "~/entity/Appeals";
import { useGetBonusQFileAppeal } from "~/pages/BonusQReport/hooks/useGetBonusQFileAppeal";
import { dateFormat } from "~/shared/lib/DateFormat/dateFormat";
import { FormWatcher } from "~/shared/ui/FormWatcher";
import {
  IAppealAdminControlFormValues,
  isSameAppealPeriodRequest,
  toAppealPeriodRequest,
} from "../model/adapter";
import s from "./AppealAdminControl.less";

interface IAppealAdminControlProps {
  isViewOnly?: boolean;
  defaultValues?: {
    period: {
      from?: Date;
      to?: Date;
    };
    date?: Date;
  };
  onPeriodChange?: () => void;
}

export const AppealAdminControl = (props: IAppealAdminControlProps) => {
  const { isViewOnly = false, defaultValues, onPeriodChange } = props;
  const { t } = useTranslation();

  const { data: dataFile, isLoading } = useGetBonusQFileAppeal();
  const { setAppealPeriod: setAppealPeriodMutation } = useSetAppeal();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef<{ data: any } | null>(null);
  const defaultPayloadRef = useRef<{ data: any } | null>(null);

  const { control, handleSubmit, watch } = useForm({
    defaultValues,
  });

  useEffect(() => {
    if (defaultPayloadRef.current) return;
    defaultPayloadRef.current = toAppealPeriodRequest(
      defaultValues || {}
    ) as any;
  }, []);

  const onDebouncedFormChange = useCallback(
    (values: IAppealAdminControlFormValues) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        const payload = toAppealPeriodRequest(values);
        if (!payload) return;

        if (
          isSameAppealPeriodRequest(payload, defaultPayloadRef.current as any)
        )
          return;
        if (isSameAppealPeriodRequest(payload, lastSentRef.current as any))
          return;

        lastSentRef.current = payload as any;
        setAppealPeriodMutation.mutate(payload, {
          onSuccess: () => {
            onPeriodChange?.();
          },
        });
      }, 500);
    },
    [setAppealPeriodMutation, onPeriodChange]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const onSubmit = (data) => {
    console.log(data, "data");
  };

  const formatPeriodDate = (date?: Date) => {
    if (!date) return "";
    return dateFormat(date, { type: "date" }) || "";
  };

  const periodFrom = formatPeriodDate(defaultValues?.period?.from);
  const periodTo = formatPeriodDate(defaultValues?.period?.to);
  const decisionDate = formatPeriodDate(defaultValues?.date);

  if (isViewOnly)
    return (
      <Flex
        className={s.container}
        justify={"space-between"}
        style={{ width: "100%" }}
      >
        <FormWatcher control={control} onChange={onDebouncedFormChange} />
        <ReportDocumentControl
          label={t("appeal.appealSubmissionOrder")}
          id={dataFile?.file?.fileId}
          link={dataFile?.file?.link}
          isLoading={isLoading}
        />
        <Flex className={s.valueContainer} gap={6} vertical>
          <p className={s.value}>
            <span className={s.title}>{t("appeal.appealPeriod")}: </span>
            {periodFrom && periodTo
              ? `${periodFrom} – ${periodTo}`
              : t("appeal.notSet")}
          </p>
          <p className={s.value}>
            <span className={s.title}>{t("appeal.decisionDate")}: </span>
            {decisionDate || t("appeal.notSetFeminine")}
          </p>
        </Flex>
      </Flex>
    );

  return (
    <form className={s.container} onSubmit={handleSubmit(onSubmit)}>
      <FormWatcher control={control} onChange={onDebouncedFormChange} />
      <Flex
        style={{ width: "100%" }}
        gap={10}
        align={"center"}
        justify={"space-between"}
      >
        <ReportDocumentControl
          label={t("appeal.appealSubmissionOrder")}
          id={dataFile?.file?.fileId}
          link={dataFile?.file?.link}
          isLoading={isLoading}
        />
        <Flex gap={4} align={"center"}>
          <label className={s.label} style={{ maxWidth: "133px" }}>
            {t("appeal.setAppealPeriod")}
          </label>
          <Controller
            control={control}
            name={"period.from"}
            render={({ value, onChange }) => (
              <DatePicker
                value={(value as Date) ?? null}
                format={"DD.MM.YYYY"}
                placeholder={""}
                onChange={(date) => onChange(date ? date.toDate() : undefined)}
              />
            )}
          />
          <span>—</span>
          <Controller
            control={control}
            name={"period.to"}
            render={({ value, onChange }) => (
              <DatePicker
                value={(value as Date) ?? null}
                format={"DD.MM.YYYY"}
                placeholder={""}
                onChange={(date) => onChange(date ? date.toDate() : undefined)}
              />
            )}
          />
        </Flex>
        <Flex gap={4} align={"center"}>
          <label className={s.label} style={{ maxWidth: "133px" }}>
            {t("appeal.decisionDate")}
          </label>
          <Controller
            control={control}
            name={"date"}
            render={({ value, onChange }) => (
              <DatePicker
                className={s.updatedDatePicker}
                value={(value as Date) ?? null}
                format={"DD.MM.YYYY"}
                placeholder={""}
                onChange={(date) => onChange(date ? date.toDate() : undefined)}
              />
            )}
          />
        </Flex>
      </Flex>
    </form>
  );
};
